import { useNavigate, useParams } from "react-router-dom"
import { MarkerImage, MarkerLocation, WalkNote } from "../../../../Interfaces"
import Map from "../../../../components/maps/Map"
import { useContext, useEffect, useMemo, useState } from "react"
import DataContext from "../../../../context/dataProvider"
import { useQuery } from "@tanstack/react-query"
import { locationService } from "../../../../services/locationService"
import PopupModal from "../../../../components/modals/PopupModal"
import { walkService } from "../../../../services/walkService"
import { getDisplayWalkTotalTime } from "../../../../utils/helper"
import { centerForModal, hasRealMarkerGps } from "../../../../utils/mapMarkers"

function PhotoTaskAnalysisBlock({
  raw,
  imageViewUrl,
  baselineImageUrl,
}: {
  raw: string
  imageViewUrl?: string | null
  baselineImageUrl?: string | null
}) {
  try {
    const data = JSON.parse(raw) as Record<string, unknown>
    const type = String(data.photoTaskType ?? "")
    const evaluatedAt = data.evaluatedAt ? String(data.evaluatedAt) : null
    if (type === "DUMPSTER") {
      return (
        <div className="space-y-2">
          {imageViewUrl && (
            <div className="overflow-hidden rounded border border-gray-200 bg-white">
              <img
                src={imageViewUrl}
                alt="Dumpster or waste area (AI review)"
                className="max-h-64 w-full object-contain"
              />
            </div>
          )}
          <div className="space-y-1">
            <p className="font-semibold text-blue-900">Dumpster / waste area (AI)</p>
            {evaluatedAt && <p className="text-gray-600">Evaluated: {new Date(evaluatedAt).toLocaleString()}</p>}
            <ul className="list-disc pl-4 space-y-0.5">
              <li>Waste area visible: {data.isDumpster === true ? "Yes" : data.isDumpster === false ? "No" : "—"}</li>
              <li>Trash around area: {data.trashAroundDumpster === true ? "Yes" : data.trashAroundDumpster === false ? "No" : "—"}</li>
              <li>Overflowing: {data.dumpsterOverflowing === true ? "Yes" : data.dumpsterOverflowing === false ? "No" : "—"}</li>
              <li>Items nearby: {data.itemsAroundDumpster === true ? "Yes" : data.itemsAroundDumpster === false ? "No" : "—"}</li>
              <li>Cleanliness (1–10): {data.cleanlinessRating != null ? String(data.cleanlinessRating) : "—"}</li>
            </ul>
          </div>
        </div>
      )
    }
    if (type === "MODEL_REFERENCE") {
      const first = data.isFirstCapture === true
      return (
        <div className="space-y-2">
          <div className="grid gap-2 md:grid-cols-2">
            {baselineImageUrl ? (
              <div className="overflow-hidden rounded border border-gray-200 bg-white p-2">
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                  Baseline
                </p>
                <img
                  src={baselineImageUrl}
                  alt="Model reference baseline"
                  className="max-h-56 w-full object-contain"
                />
              </div>
            ) : null}
            {imageViewUrl ? (
              <div className="overflow-hidden rounded border border-gray-200 bg-white p-2">
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                  Walk Photo
                </p>
                <img
                  src={imageViewUrl}
                  alt="Model reference capture (AI review)"
                  className="max-h-56 w-full object-contain"
                />
              </div>
            ) : null}
          </div>
          <div className="space-y-1">
            <p className="font-semibold text-blue-900">Model reference photo (AI)</p>
            {evaluatedAt && <p className="text-gray-600">Evaluated: {new Date(evaluatedAt).toLocaleString()}</p>}
            <ul className="list-disc pl-4 space-y-0.5">
              <li>Baseline capture: {first ? "Yes (established reference)" : "No"}</li>
              {!first && (
                <li>
                  Same angle as reference:{" "}
                  {data.sameAngleAsReference === true
                    ? "Yes"
                    : data.sameAngleAsReference === false
                      ? "No"
                      : "—"}
                </li>
              )}
              {!first && data.angleNotes != null && String(data.angleNotes).trim() !== "" && (
                <li>Angle notes: {String(data.angleNotes)}</li>
              )}
              <li>
                Significant weeds/trash/debris:{" "}
                {data.significantWeedsOrTrash === true
                  ? "Yes"
                  : data.significantWeedsOrTrash === false
                    ? "No"
                    : "—"}
              </li>
              {data.debrisNotes != null && String(data.debrisNotes).trim() !== "" && (
                <li>Debris notes: {String(data.debrisNotes)}</li>
              )}
            </ul>
          </div>
        </div>
      )
    }
    if (type === "BUSINESS_LICENSE") {
      return (
        <div className="space-y-1">
          <p className="font-semibold text-blue-900">Business license (AI)</p>
          {evaluatedAt && <p className="text-gray-600">Evaluated: {new Date(evaluatedAt).toLocaleString()}</p>}
          <ul className="list-disc pl-4 space-y-0.5">
            <li>Looks like license: {data.appearsToBeBusinessLicense === true ? "Yes" : data.appearsToBeBusinessLicense === false ? "No" : "—"}</li>
            {data.businessName != null && <li>Business: {String(data.businessName)}</li>}
            {data.expirationDate != null && <li>Expires: {String(data.expirationDate)}</li>}
            {data.licenseNumber != null && <li>License #: {String(data.licenseNumber)}</li>}
            {data.issuingAuthority != null && <li>Issued by: {String(data.issuingAuthority)}</li>}
            {data.additionalDetails != null && String(data.additionalDetails).trim() !== "" && (
              <li>Details: {String(data.additionalDetails)}</li>
            )}
          </ul>
        </div>
      )
    }
    return <pre className="whitespace-pre-wrap break-all text-[11px]">{raw}</pre>
  } catch {
    return <p className="text-gray-600">AI result (unparseable)</p>
  }
}

export default function SingleWalkReport() {
  const navigate = useNavigate()
  const { id, walkReportId } = useParams()
  const { data: walkReport, isLoading } = useQuery(["walkReport", walkReportId], () => walkService.getOneReport(Number(walkReportId)), {
    enabled: walkReportId !== undefined,
  })

  const [modalContent, setModalContent] = useState<any>()
  const [isOpen, setIsOpen] = useState(false)
  const [mapModalContent, setMapModalContent] = useState<any>()
  const [isMapModalOpen, setIsMapModalOpen] = useState(false)
  const { currentLocation } = useContext(DataContext)
  const [markerImages, setMarkerImages] = useState<MarkerImage[]>([])

  const walkLocationId = walkReport?.walk?.locationId
  const locationFromReport = walkReport?.walk?.location
  const locationFromContext =
    walkLocationId != null &&
    currentLocation?.id != null &&
    currentLocation.id === walkLocationId
      ? currentLocation
      : undefined
  const needsLocationFetch =
    walkLocationId != null && !locationFromReport && !locationFromContext

  const { data: fetchedLocation, isLoading: isLocationLoading } = useQuery(
    ["walkReportLocation", walkLocationId],
    () => locationService.getOne(walkLocationId!),
    { enabled: needsLocationFetch && walkLocationId != null },
  )

  const locationData =
    locationFromReport ?? locationFromContext ?? fetchedLocation ?? undefined
  const coordinates = useMemo<google.maps.LatLngLiteral>(
    () => ({
      lat: Number(locationData?.latitude || 0),
      lng: Number(locationData?.longitude || 0),
    }),
    [locationData?.latitude, locationData?.longitude],
  )
  const [zoom, setZoom] = useState<number>(locationData?.zoomLevel || 18)

  // Map pins / polyline must use walk.markers (lat, lng, label). CompletedMarker rows
  // do not carry geometry and broke the map when used here.
  const baseMarkers = useMemo((): MarkerLocation[] => {
    if (!walkReport?.walk?.markers?.length) return []
    return walkReport.walk.markers
  }, [walkReport?.walk?.markers])

  const markers = useMemo((): MarkerLocation[] => {
    const walkNotes = walkReport?.walkNote ?? []
    const resolveMarkerId = (marker: MarkerLocation): number | undefined => {
      const m = marker as MarkerLocation & { markerId?: number }
      if (typeof m.markerId === "number") return m.markerId
      return marker.id
    }
    return baseMarkers
      .map((marker) => {
        const mid = resolveMarkerId(marker)
        const notes = mid != null ? walkNotes.filter((note) => note.markerId === mid) : []
        const images = mid != null ? markerImages.filter((image) => image.markerId === mid) : []
        return { ...marker, notes, images }
      })
      .sort((a, b) => a.label - b.label)
  }, [baseMarkers, walkReport?.walkNote, markerImages])

  // Seed image list when report loads; presigned URLs applied in the next effect.
  useEffect(() => {
    if (walkReport) {
      setMarkerImages(walkReport.markerImage || [])
    }
  }, [walkReport])

  useEffect(() => {
    if (!walkReport?.markerImage?.length) return
    const tasks = walkReport.markerImage.map(async (image) => {
      const presignedViewableUrl = await walkService.getViewableMarkerImage(
        image.walkReportId,
        image.markerId,
        image.imageName,
      )
      return { ...image, imageUrl: presignedViewableUrl }
    })
    Promise.all(tasks).then(setMarkerImages)
  }, [walkReport?.markerImage])

  useEffect(() => {
    if (locationData?.zoomLevel != null) {
      setZoom(locationData.zoomLevel)
    }
  }, [locationData?.zoomLevel])

  if (isLoading || !walkReport) {
    return (
      <div className="w-full min-h-full flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading walk report...</p>
        </div>
      </div>
    )
  }

  if (!walkReport.walk?.locationId) {
    return (
      <div className="w-full min-h-full flex justify-center items-center p-4 text-gray-600">
        This walk report is missing location information.
      </div>
    )
  }

  if (!locationData) {
    if (needsLocationFetch && isLocationLoading) {
      return (
        <div className="w-full min-h-full flex justify-center items-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading map location...</p>
          </div>
        </div>
      )
    }
    return (
      <div className="w-full min-h-full flex justify-center items-center p-4 text-gray-600">
        Could not load location for this walk. Try opening the location first, then the report again.
      </div>
    )
  }

  const totalWalkTime = getDisplayWalkTotalTime(walkReport)

  const finishedAt = walkReport?.finishedAt ? new Date(walkReport?.finishedAt).toLocaleTimeString() : "Not Completed"

  function handleNoteClick(notes: WalkNote[]) {
    if (!notes) return
    setModalContent(
      <div className="flex flex-wrap sm:rounded-lg p-1 bg-secondary-extra-light max-w-3xl text-lg">
        <div className="w-full flex flex-col overflow-auto max-h-[800px]">
          {notes.map((note: WalkNote) => (
            <ul key={note.id}>
              <li className="p-2 rounded-md shadow-md">{note.note}</li>
            </ul>
          ))}
        </div>
      </div>
    )
    setIsOpen(true)
  }

  function handleImageClick(images: MarkerImage[]) {
    if (!images) return

    setModalContent(
      <div className="flex flex-col overflow-auto max-h-[750px]">
        {images.map((image: MarkerImage) => (
          // contain images and make them scrollable if they are too big or there are too many
          <div className="object-contain h-full max-w-5xl" key={image.id}>
            {image.imageUrl ? (
              <img src={image.imageUrl} alt={image.imageName} />
            ) : (
              <div className=" h-72 w-full min-w-[400px] bg-slate-300 flex justify-center items-center px-4">
                <h2 className="font-bold text-center">No Image Available</h2>
              </div>
            )}
          </div>
        ))}
      </div>
    )
    setIsOpen(true)
  }

  function handleCloseModal() {
    setIsOpen(false)
    setModalContent(null)
  }

  function handleCloseMapModal() {
    setIsMapModalOpen(false)
    setMapModalContent(null)
  }

  function handleMarkerMapClick(marker: MarkerLocation) {
    const markerCoordinates = centerForModal(marker, coordinates)

    setMapModalContent(
      <div className="w-full h-full flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">
            Marker {marker.label} Location
            {marker.unitNumberRange && (
              <span className="text-gray-600 ml-2">({marker.unitNumberRange})</span>
            )}
          </h3>
          <button
            onClick={handleCloseMapModal}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>
        <div className="flex-grow relative" style={{ height: '800px' }}>
          <Map
            name={""}
            zoom={18}
            setZoom={() => { }}
            markers={[marker]}
            coordinates={markerCoordinates}
            walkNotes={[]}
            walkImages={[]}
            handleNoteClick={() => { }}
            handleImageClick={() => { }}
            animate={false}
          />
        </div>
      </div>
    )
    setIsMapModalOpen(true)
  }

  return (
    <div className="w-full min-h-full flex justify-center">
      <div className="w-full flex flex-col">
        <section className="bg-secondary-extra-light p-2 md:rounded-lg mb-2 shadow-lg h-full">
          <div className="bg-primary-dark h-full flex flex-col">
            <div className=" text-white rounded-lg p-4 flex justify-between">
              <div className="flex">
                <div className="mr-6">
                  <h3 className=" font-extrabold">{walkReport.user ? `${walkReport.user.firstName || ''} ${walkReport.user.lastName || ''}`.trim() || 'Unknown User' : 'Unknown User'}</h3>
                  <h4 className="">{walkReport.walk?.name || 'Unknown Walk'}</h4>
                  <h4 className="">Date: {new Date(walkReport.finishedAt || walkReport.createdAt).toLocaleDateString()}</h4>
                </div>
                <div>
                  <h3 className="">{" "}</h3>
                  <h4 className=" pr-2">Started: {new Date(walkReport.startedAt).toLocaleTimeString()}</h4>
                  <h4 className="">Finished: {finishedAt}</h4>
                  <h4 className="">
                    Total Time: {totalWalkTime || "Not Completed"}
                  </h4>
                </div>
              </div>

              <div>
                <div className="flex justify-end p-2">
                  <button className="rounded-lg p-2 ml-3 text-white text-center bg-primary border-sedondary hover:border-2 hover:border-primary-light" onClick={() => navigate(`/app/locations/${id}`)}>
                    Walk Reports
                  </button>
                </div>
              </div>
            </div>

            {/* Completed Markers List */}
            {walkReport.CompletedMarker && walkReport.CompletedMarker.length > 0 && (
              <div className="bg-white p-4 mx-4 mb-4 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Completed Markers</h3>
                <div className="space-y-2">
                  {walkReport.CompletedMarker
                    .map((completedMarker) => {
                      // Find the corresponding marker details from walk.markers
                      const markerDetails = walkReport.walk?.markers?.find(marker => marker.id === completedMarker.markerId)
                      // Find images for this marker
                      const markerImages = walkReport.markerImage?.filter(img => img.markerId === completedMarker.markerId) || []

                      return {
                        completedMarker,
                        markerDetails,
                        markerImages,
                        label: markerDetails?.label || completedMarker.markerId
                      }
                    })
                    .sort((a, b) => Number(a.label) - Number(b.label)) // Sort numerically
                    .map(({ completedMarker, markerDetails, markerImages, label }) => (
                      <div key={completedMarker.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                        <div className="flex items-center space-x-4">
                          <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-semibold">
                            {label}
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">
                              Marker {label}
                              {markerDetails?.unitNumberRange && (
                                <span className="text-gray-600 ml-2">({markerDetails.unitNumberRange})</span>
                              )}
                            </p>
                            <p className="text-sm text-gray-600">
                              Arrived: {new Date(completedMarker.arrivedAt).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          {/* Map Icon */}
                          {markerDetails && (
                            <button
                              onClick={() => handleMarkerMapClick(markerDetails)}
                              className="p-2 text-gray-600 hover:text-primary hover:bg-gray-100 rounded-full transition-colors"
                              title={
                                hasRealMarkerGps(markerDetails)
                                  ? "View on map"
                                  : "Approximate site location (no GPS for this marker)"
                              }
                            >
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                              </svg>
                            </button>
                          )}

                          {/* Picture Match Status */}
                          {completedMarker.isDoor && markerImages.length > 0 && (
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium text-gray-700">Picture:</span>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                ✓ Taken
                              </span>
                            </div>
                          )}
                          {markerImages.length > 0 && (
                            <button
                              onClick={() => handleImageClick(markerImages)}
                              className="inline-flex items-center gap-2 rounded border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100"
                              title="View walk photos for this marker"
                            >
                              <span>Photos</span>
                              <span className="rounded bg-gray-100 px-1.5 py-0.5">{markerImages.length}</span>
                            </button>
                          )}

                          {/* Unit Match Status */}
                          {completedMarker.targetUnit && (
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium text-gray-700">Unit:</span>
                              {completedMarker.picturedUnit === completedMarker.targetUnit ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  ✓ Matched
                                </span>
                              ) : (
                                <div className="flex flex-col space-y-1">
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                    ⚠ Mismatch
                                  </span>
                                  <div className="text-xs text-gray-600">
                                    <div>Requested: {completedMarker.targetUnit}</div>
                                    <div>Detected: {completedMarker.picturedUnit || 'None'}</div>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {completedMarker.photoTaskAnalysis && (
                            <div className="mt-2 max-w-md rounded border border-blue-100 bg-blue-50/80 px-3 py-2 text-xs text-gray-800">
                              <PhotoTaskAnalysisBlock
                                raw={completedMarker.photoTaskAnalysis}
                                imageViewUrl={completedMarker.photoTaskViewUrl}
                                baselineImageUrl={markerDetails?.modelReferenceUrl ?? null}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            <div className="flex min-h-0 flex-grow flex-col">
              <div className="relative w-full min-h-[min(50vh,800px)] h-[50vh] max-h-[85vh] flex-grow bg-secondary-extra-light shadow-lg">
                <Map name={""} zoom={zoom} setZoom={setZoom} markers={markers} coordinates={coordinates} walkNotes={walkReport.walkNote} walkImages={walkReport.markerImage} handleNoteClick={handleNoteClick} handleImageClick={handleImageClick} animate={true} />
              </div>
            </div>
          </div>
        </section>
      </div>
      <PopupModal isOpen={isOpen} onCancel={handleCloseModal} className={``}>
        {modalContent}
      </PopupModal>
      <PopupModal isOpen={isMapModalOpen} onCancel={handleCloseMapModal} className={`max-w-6xl w-full`}>
        {mapModalContent}
      </PopupModal>
    </div>
  )
}
